import { useState, useRef, useEffect, useCallback } from 'react';
import { Image as ImageIcon, Sparkles, User, Heart } from 'lucide-react';

/**
 * MobileBottomNav Component - 2026 UX/UI Design
 * Features:
 * - Animated pill indicator between tabs
 * - Haptic feedback on tap (via navigator.vibrate)
 * - Badge animations for notifications
 * - Smooth transitions with spring physics
 */
export function MobileBottomNav({
  activeTab,
  onTabChange,
  favoritesCount = 0,
  visible = true,
  enableHaptics = true,
}) {
  const [pillStyle, setPillStyle] = useState({ left: 0, width: 0 });
  const [animatingBadge, setAnimatingBadge] = useState(false);
  const navRef = useRef(null);
  const tabRefs = useRef({});

  const tabs = [
    { id: 'all', label: 'All', icon: ImageIcon, activeColor: 'from-slate-800 to-slate-700', hoverColor: 'slate' },
    { id: 'tags', label: 'AI Search', icon: Sparkles, activeColor: 'from-orange-500 to-red-500', hoverColor: 'orange' },
    { id: 'faces', label: 'Find Me', icon: User, activeColor: 'from-blue-600 to-blue-500', hoverColor: 'blue' },
    { id: 'favorites', label: 'Favorites', icon: Heart, activeColor: 'from-[#BF212F] to-[#F26B38]', hoverColor: 'red' }
  ];

  // Calculate pill position based on active tab
  const updatePillPosition = useCallback(() => {
    const activeTabEl = tabRefs.current[activeTab];
    if (activeTabEl && navRef.current) {
      const navRect = navRef.current.getBoundingClientRect();
      const tabRect = activeTabEl.getBoundingClientRect();

      setPillStyle({
        left: tabRect.left - navRect.left + 4,
        width: tabRect.width - 8,
      });
    }
  }, [activeTab]);

  // Update pill position on tab change or resize
  useEffect(() => {
    updatePillPosition();
    window.addEventListener('resize', updatePillPosition);
    return () => window.removeEventListener('resize', updatePillPosition);
  }, [updatePillPosition]);

  // Animate badge on favorites count change
  useEffect(() => {
    if (favoritesCount > 0) {
      setAnimatingBadge(true);
      const timer = setTimeout(() => setAnimatingBadge(false), 400);
      return () => clearTimeout(timer);
    }
  }, [favoritesCount]);

  // Handle tab click with haptic feedback
  const handleTabClick = (tabId) => {
    // Trigger haptic feedback
    if (enableHaptics && navigator.vibrate) {
      navigator.vibrate(10);
    }
    onTabChange(tabId);
  };

  // Get active tab data
  const activeTabData = tabs.find(t => t.id === activeTab) || tabs[0];

  return (
    <nav
      ref={navRef}
      className={`
        fixed bottom-0 left-0 right-0
        bg-white/95 dark:bg-slate-900/95
        backdrop-blur-2xl
        border-t border-slate-200/50 dark:border-slate-700/50
        z-50 sm:hidden pb-safe
        transition-transform duration-300 ease-out
        ${visible ? 'translate-y-0' : 'translate-y-full'}
      `}
    >
      {/* Animated pill indicator */}
      <div
        className={`
          absolute top-1 h-[calc(100%-env(safe-area-inset-bottom)-8px)]
          bg-gradient-to-r ${activeTabData.activeColor}
          rounded-2xl shadow-lg
          transition-all duration-300 ease-[cubic-bezier(0.22,1.15,0.36,1)]
          z-0
        `}
        style={{
          left: pillStyle.left,
          width: pillStyle.width,
        }}
      />

      {/* Top indicator line */}
      <div
        className={`
          absolute top-0 h-0.5
          bg-gradient-to-r ${activeTabData.activeColor}
          rounded-full
          transition-all duration-300 ease-[cubic-bezier(0.22,1.15,0.36,1)]
        `}
        style={{
          left: pillStyle.left + 8,
          width: pillStyle.width - 16,
        }}
      />

      <div className="flex justify-around items-stretch h-16 px-2 relative z-10">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;

          return (
            <button
              key={tab.id}
              ref={(el) => tabRefs.current[tab.id] = el}
              onClick={() => handleTabClick(tab.id)}
              className={`
                flex-1 flex flex-col items-center justify-center py-2 relative
                min-w-[64px] min-h-[44px]
                transition-all duration-200 touch-manipulation
                active:scale-95
                ${isActive ? 'text-white' : 'text-slate-500 dark:text-slate-400'}
              `}
              aria-label={tab.label}
              aria-selected={isActive}
              role="tab"
            >
              {/* Icon with animation */}
              <Icon
                className={`
                  w-5 h-5
                  transition-all duration-300
                  ${isActive ? 'scale-110 drop-shadow-md' : 'group-hover:scale-105'}
                `}
              />

              {/* Label */}
              <span
                className={`
                  text-[10px] mt-1 font-semibold
                  transition-all duration-200
                  ${isActive ? 'opacity-100' : 'opacity-70'}
                `}
              >
                {tab.label}
              </span>

              {/* Favorites badge with animation */}
              {tab.id === 'favorites' && favoritesCount > 0 && (
                <span
                  className={`
                    absolute top-0.5 right-1/4
                    bg-gradient-to-r from-[#BF212F] to-[#F26B38]
                    text-white text-[10px] font-bold
                    rounded-full min-w-[18px] h-[18px]
                    flex items-center justify-center px-1
                    shadow-md z-20 border border-white/30
                    ${animatingBadge ? 'animate-heart-burst' : ''}
                  `}
                >
                  {favoritesCount > 99 ? '99+' : favoritesCount}
                </span>
              )}

              {/* Ripple effect on tap */}
              <span
                className={`
                  absolute inset-0 rounded-2xl
                  bg-current opacity-0
                  pointer-events-none
                  active:opacity-10 active:scale-90
                  transition-all duration-150
                `}
              />
            </button>
          );
        })}
      </div>
    </nav>
  );
}

export default MobileBottomNav;
